import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useQueryClient, useMutation } from "@tanstack/react-query";

import { useAnalytics, useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { useUpdateUserConfig } from "@/shared/domain/config/hooks";
import * as GetUserConfig from "@/shared/domain/config/hooks/useUserConfig";
import { LayoutRecord } from "@/shared/domain/layouts";
import { layoutQueryKeys } from "@/shared/domain/layouts/hooks";

interface DeleteLayoutConfirmationProps {
  isOpen: boolean;
  layout: LayoutRecord | null;
  onClose: () => void;
  onExited: () => void;
  onSuccess: (deletedLayout: LayoutRecord) => void;
}

export function DeleteLayoutConfirmation(props: DeleteLayoutConfirmationProps) {
  const { isOpen, layout, onClose, onExited, onSuccess } = props;

  const { trackEvent } = useAnalytics();

  const deleteLayout = useDeleteLayout();

  const handleConfirmDelete = () => {
    if (layout) {
      deleteLayout.mutate(layout.layout_id, {
        onSuccess: () => {
          onSuccess(layout);
        },
      });
    }
  };

  const closeDialog = () => {
    trackEvent("DeleteLayoutConfirmationCanceled", {
      layoutId: layout?.layout_id ?? "",
      layoutName: layout?.name ?? "",
    });
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={closeDialog}
      TransitionProps={{ onExited }}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle>Delete &quot;{layout?.name ?? "Layout"}&quot;</DialogTitle>
      <DialogContent>
        Are you sure you want to delete this layout? This action cannot be
        undone.
        {deleteLayout.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteLayout.error instanceof Error
              ? deleteLayout.error.message
              : "Failed to delete layout"}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <RobotoButton
          eventName="DeleteLayoutConfirmationCanceled"
          eventProperties={{
            layoutId: layout?.layout_id ?? "",
            layoutName: layout?.name ?? "",
          }}
          onClick={onClose}
          disabled={deleteLayout.isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="DeleteLayoutConfirmationClicked"
          eventProperties={{
            layoutId: layout?.layout_id ?? "",
            layoutName: layout?.name ?? "",
          }}
          onClick={handleConfirmDelete}
          color="error"
          variant="contained"
          disabled={deleteLayout.isPending}
          startIcon={
            deleteLayout.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : null
          }
        >
          {deleteLayout.isPending ? "Deleting..." : "Delete"}
        </RobotoButton>
      </DialogActions>
    </Dialog>
  );
}

function useDeleteLayout() {
  const { configService, layoutService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();
  const updateUserConfig = useUpdateUserConfig();

  return useMutation({
    mutationFn: async (layoutId: string) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }
      return layoutService.deleteLayout(layoutId, currentOrganization.org_id);
    },
    onSuccess: async (_, layoutId) => {
      const defaultLayoutId = await queryClient.ensureQueryData({
        queryKey: GetUserConfig.getQueryKey("user_default_layout_id"),
        queryFn: () => configService.getUserConfig("user_default_layout_id"),
      });

      // Clear the default layout config if the deleted layout was the user's default layout
      if (defaultLayoutId === layoutId) {
        updateUserConfig.mutate({
          key: "user_default_layout_id",
          value: "",
        });
      }

      return queryClient.invalidateQueries({
        queryKey: layoutQueryKeys.lists(),
      });
    },
  });
}
